import { useEffect, useState } from 'react';
import { ProgressBar, Spinner } from 'react-bootstrap';
import { ButtonV2 as Button } from '@praktio/common-ui';

import './OverlayProgressBar.scss';

interface IProps {
  progress: number;
  isComplete?: boolean;
  onCancel: () => void;
  onComplete?: () => void;
}

// How long the "Complete!" message + checkmark stay visible before the
// fade-out begins. 600ms feels snappy but gives enough time to read.
// The fade-out itself is a 400ms CSS opacity transition (see SCSS).
const HOLD_MS = 600;

const OverlayProgressBar = ({ progress, isComplete, onCancel, onComplete }: IProps) => {
  const [fadeOut, setFadeOut] = useState(false);

  // Once loading finishes (isComplete=true), hold the "Complete!" state
  // for HOLD_MS, then trigger the CSS fade by setting fadeOut=true.
  useEffect(() => {
    if (!isComplete) return;

    const timer = setTimeout(() => setFadeOut(true), HOLD_MS);
    return () => clearTimeout(timer);
  }, [isComplete]);

  // After the CSS opacity transition ends, notify the parent to unmount
  // the overlay. This is wired to useOverlayTransition's handleComplete.
  const handleTransitionEnd = () => {
    if (fadeOut && onComplete) {
      onComplete();
    }
  };

  const className = [
    'OverlayProgressBar d-flex w-100 h-100 align-items-center justify-content-center',
    fadeOut ? 'OverlayProgressBar--fadeOut' : '',
  ].join(' ');

  return (
    <div
      className={className}
      onTransitionEnd={handleTransitionEnd}
      role="status"
      aria-live="polite"
    >
      <div className="OverlayProgressBar_Content">
        {isComplete ? (
          <div className="OverlayProgressBar_CheckIcon" aria-hidden="true">
            &#10003;
          </div>
        ) : (
          <Spinner animation="border" role="status" className="OverlayProgressBar_Spinner">
            <span className="sr-only">Loading...</span>
          </Spinner>
        )}
        <div className="OverlayProgressBar_Label">
          {isComplete ? 'Complete!' : `Loading report data... ${Math.round(progress)}%.`}
          {!isComplete && (
            <div className="OverlayProgressBar_Sublabel">This might take a few minutes.</div>
          )}
        </div>
        <ProgressBar now={isComplete ? 100 : progress} aria-label="Report loading progress" />
        {!isComplete && (
          <Button
            variant="outline"
            onClick={onCancel}
            className="mt-3"
            aria-label="Cancel report loading"
          >
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
};

export default OverlayProgressBar;
